import { CLIMBING_STYLES } from './constants.js';
import { processAttempts, calculateGradeStats } from './attemptUtils.js';
import { isFirstTimeAscent } from './ascentTypes.js';  // Add this import

function sigmoid(x) {
  return 1 / (1 + Math.exp(-x));
}

export function prepareRegressionData(data, climbingStyle, yearMode) {
  const isAllTime = yearMode === 'all';
  const targetYear = isAllTime ? null : parseInt(yearMode, 10);

  if (!data?.length) return null;

  // First filter by climbing style and valid grades
  const styleFilteredAttempts = data.filter(attempt => {
    const routeStyle = attempt["Route Gear Style"];

    // Check if the attempt matches current style
    if (climbingStyle === 'boulder') {
      if (routeStyle !== "Boulder") return false;
    } else if (climbingStyle === 'sport') {
      if (routeStyle !== "Sport") return false;
    } else if (climbingStyle === 'trad') {
      if (routeStyle !== "Trad") return false;
    }

    // Validate grades based on style
    const grade = climbingStyle === 'boulder' ? attempt.vgrade : attempt.ewbanks;
    if (grade === null || isNaN(grade)) return false;

    if (climbingStyle === 'boulder') {
      return grade >= 0 && grade <= 17;
    } else {
      // Both sport and trad use Ewbanks
      return grade >= 10 && grade <= 39;
    }
  });

  // Then filter by year and shouldCount
  const validAttempts = isAllTime ?
    styleFilteredAttempts.filter(attempt => attempt.shouldCount) :
    styleFilteredAttempts.filter(attempt => attempt.year === targetYear && attempt.shouldCount);

  // Calculate grade statistics
  const gradeStats = {};
  
  validAttempts.forEach(attempt => {
    const grade = climbingStyle === 'boulder' ? 
      Number(attempt.vgrade) : 
      Number(attempt.ewbanks);
    
    if (!gradeStats[grade]) {
      gradeStats[grade] = {
        grade,
        totalRoutes: new Set(),
        flashedRoutes: new Set()  
      };
    }
    
    const routeId = attempt["Route ID"];
    gradeStats[grade].totalRoutes.add(routeId);
    
    if (isFirstTimeAscent(attempt["Ascent Type"])) {  // Changed this line
      gradeStats[grade].flashedRoutes.add(routeId);
    }
  });

  // Transform grade stats into arrays for regression
  const attempts = [];
  
  Object.entries(gradeStats).forEach(([grade, stats]) => {
    const numericGrade = Number(grade);
    
    stats.totalRoutes.forEach(routeId => {
      const attempt = validAttempts.find(entry => 
        entry['Route ID'] === routeId
      );
      
      if (!attempt) return;
      
      const isFirstGo = isFirstTimeAscent(attempt['Ascent Type']);  // Changed this line
      
      attempts.push({
        grade: numericGrade,
        success: isFirstGo,
        year: isAllTime ? 'all' : targetYear
      });
    });
  });

  // Create arrays for regression
  const x = attempts.map(a => a.grade);
  const y = attempts.map(a => a.success ? 1 : 0);
  const years = attempts.map(a => a.year);

  if (x.length < 5 || !y.includes(1)) {
    return null;
  }

  return {
    x,
    y,
    years,
    gradeStats,
    attempts,
    processedData: validAttempts
  };
}



/**
 * Perform logistic regression
 */
export function logisticRegression(x, y, year, climbingStyle, isAllTime, learningRate = 0.1, maxIterations = 10000, threshold = 1e-6) {
  // Style-specific priors and parameters
  const styleConfig = climbingStyle === CLIMBING_STYLES.BOULDER ? 
    {
      mPrior: -1.0,    // Steeper curve for boulders
      lambda: 10,      // Regularization strength
      learningRate: 0.05,
      initialB: 4
    } : {
      mPrior: -0.7,    // Gentler curve for sport
      lambda: 10,      // Regularization strength
      learningRate: 0.1,
      initialB: 20
    };

  // Initialize with style-specific prior
  let m = styleConfig.mPrior;
  let b = {};
  let iterations = 0;
  
  // Use the actual year values for initialization
  const uniqueYears = isAllTime ? ['all'] : [...new Set(year)];
  
  uniqueYears.forEach(yr => {
    b[yr] = styleConfig.initialB;
  });

  const successfulAttempts = y.filter(val => val === 1).length;
  const failedAttempts = y.filter(val => val === 0).length;
  
  if (x.length < 5 || successfulAttempts === 0) {
    return null;
  }

  for (let i = 0; i < maxIterations; i++) {
    let dM = 0;
    let dB = {};
    uniqueYears.forEach(yr => {
      dB[yr] = 0;
    });

    for (let j = 0; j < x.length; j++) {
      const yearKey = isAllTime ? 'all' : year[j];
      const prediction = sigmoid(m * (x[j] - b[yearKey]));
      dM += (prediction - y[j]) * (x[j] - b[yearKey]);
      dB[yearKey] += (y[j] - prediction) * m;
    }

    // Add regularization with style-specific prior
    dM += styleConfig.lambda * (m - styleConfig.mPrior);

    const mChange = styleConfig.learningRate * (dM / x.length);
    m -= mChange;

    let bChangeMax = 0;
    uniqueYears.forEach(yr => {
      const relevantDataCount = isAllTime ? x.length : x.filter((_, idx) => year[idx] === yr).length;
      
      if (relevantDataCount > 0) {
        const bChange = styleConfig.learningRate * dB[yr] / relevantDataCount;
        b[yr] -= bChange;
        bChangeMax = Math.max(bChangeMax, Math.abs(bChange));
      }
    });

    iterations++;
    if (Math.abs(mChange) < threshold && bChangeMax < threshold) {
      break;
    }
  }

  // Validate results based on climbing style
  const slopeRange = climbingStyle === CLIMBING_STYLES.BOULDER ?
    { min: -1.5, max: -0.5 } :  // Boulder range
    { min: -1.2, max: -0.3 };   // Sport range

  if (m < slopeRange.min || m > slopeRange.max) {
    // Fall back to prior if estimate is unreasonable
    m = styleConfig.mPrior;
  }

  if (climbingStyle === CLIMBING_STYLES.BOULDER) {
    for (const yr in b) {
      if (b[yr] < 0 || b[yr] > 17) {
        return null;
      }
    }
  }

  return { m, b };
}

/**
 * Calculate first go grade and regression statistics
 */
export function analyzeFirstGoPerformance(data, climbingStyle, yearMode) {
  const isAllTime = yearMode === 'all';

  if (!data?.length) {
    return {
      currentGrade: null,
      message: 'Not enough data',
      reason: 'No attempts found'
    };
  }

  // Use shared data preparation
  const { x, y, years, gradeStats } = prepareRegressionData(data, climbingStyle, yearMode);

  const regressionResult = logisticRegression(x, y, years, climbingStyle);

  if (!regressionResult) {
    return {
      currentGrade: null,
      message: 'Not enough data',
      reason: x.length < 5 ? 'Need at least 5 attempts' : 'Need at least one flash/onsight'
    };
  }

  const { m, b } = regressionResult;
  const gradeValue = isAllTime ? b['all'] : b[Object.keys(b)[0]];
  
  return {
    currentGrade: parseFloat(gradeValue?.toFixed(1)),
    gradeByYear: Object.fromEntries(
      Object.entries(b).map(([year, grade]) => [
        year,
        parseFloat(grade.toFixed(1))
      ])
    ),
    slope: m,
    dataPoints: x.length,
    gradeStats // Include for plotting
  };
}